import {
  ActionIcon,
  Button,
  Divider,
  Group,
  NumberInput,
  ScrollArea,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { LuPlus, LuTrash2 } from 'react-icons/lu'
import { CurrencyText } from './CurrencyText'

export interface LineItem {
  description: string
  quantity: number
  unitPriceIdr: number
}

interface Props {
  items: LineItem[]
  onChange: (items: LineItem[]) => void
  taxRate?: number // percent, e.g. 11 for 11%
  onTaxRateChange?: (rate: number) => void
  showTaxControl?: boolean
  addLabel?: string
}

/**
 * Editable line items table for Quote/Invoice/Contract.
 * - Each row: description, quantity, unit price (IDR), amount (auto-calc).
 * - Footer shows subtotal, tax (configurable rate), total.
 */
export function LineItemEditor({
  items,
  onChange,
  taxRate = 11,
  onTaxRateChange,
  showTaxControl = true,
  addLabel = 'Add line',
}: Props) {
  const update = (idx: number, patch: Partial<LineItem>) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it))
    onChange(next)
  }
  const remove = (idx: number) => onChange(items.filter((_, i) => i !== idx))
  const add = () => onChange([...items, { description: '', quantity: 1, unitPriceIdr: 0 }])

  const subtotal = items.reduce((sum, it) => sum + it.quantity * it.unitPriceIdr, 0)
  const tax = (subtotal * (taxRate ?? 0)) / 100
  const total = subtotal + tax

  return (
    <Stack gap="md">
      <ScrollArea offsetScrollbars>
        <Table withTableBorder withColumnBorders verticalSpacing="sm" miw={640}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: '50%' }}>Description</Table.Th>
              <Table.Th ta="right">Qty</Table.Th>
              <Table.Th ta="right">Unit Price (IDR)</Table.Th>
              <Table.Th ta="right">Amount</Table.Th>
              <Table.Th style={{ width: 40 }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Text c="dimmed" ta="center" size="sm" py="md">
                    No line items yet. Click "{addLabel}" below.
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {items.map((it, idx) => {
              const amount = it.quantity * it.unitPriceIdr
              return (
                // biome-ignore lint/suspicious/noArrayIndexKey: editable line items lack stable IDs; index works for now
                <Table.Tr key={idx}>
                  <Table.Td>
                    <TextInput
                      placeholder="Description"
                      value={it.description}
                      onChange={(e) => update(idx, { description: e.currentTarget.value })}
                      variant="unstyled"
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={it.quantity}
                      onChange={(v) => update(idx, { quantity: typeof v === 'number' ? v : Number(v) || 0 })}
                      min={0}
                      step={1}
                      decimalScale={2}
                      variant="unstyled"
                      styles={{ input: { textAlign: 'right' } }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <NumberInput
                      value={it.unitPriceIdr}
                      onChange={(v) => update(idx, { unitPriceIdr: typeof v === 'number' ? v : Number(v) || 0 })}
                      min={0}
                      step={1000}
                      thousandSeparator=","
                      decimalScale={0}
                      variant="unstyled"
                      styles={{ input: { textAlign: 'right' } }}
                    />
                  </Table.Td>
                  <Table.Td>
                    <CurrencyText idr={amount} ta="right" fw={500} />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon variant="subtle" color="red" onClick={() => remove(idx)} aria-label="Remove">
                      <LuTrash2 size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <Group justify="space-between">
        <Button variant="default" leftSection={<LuPlus size={14} />} size="xs" onClick={add}>
          {addLabel}
        </Button>
      </Group>

      <Divider />

      <Stack gap={4} align="flex-end">
        <Group gap="md">
          <Text c="dimmed" size="sm">
            Subtotal:
          </Text>
          <CurrencyText idr={subtotal} fw={500} miw={140} ta="right" />
        </Group>
        <Group gap="md" align="center">
          <Text c="dimmed" size="sm">
            Tax:
          </Text>
          {showTaxControl && onTaxRateChange ? (
            <NumberInput
              value={taxRate}
              onChange={(v) => onTaxRateChange(typeof v === 'number' ? v : Number(v) || 0)}
              min={0}
              max={100}
              step={1}
              suffix="%"
              size="xs"
              w={100}
            />
          ) : (
            <Text size="sm">{taxRate}%</Text>
          )}
          <CurrencyText idr={tax} fw={500} miw={140} ta="right" />
        </Group>
        <Group gap="md">
          <Text fw={700} size="sm">
            Total:
          </Text>
          <CurrencyText idr={total} fw={700} size="lg" miw={140} ta="right" c="laut.7" />
        </Group>
      </Stack>
    </Stack>
  )
}
